// Hücre editörü "Geçmiş" sekmesi — KpiCellEditor.tsx'ten ayrıldı (güvenlik/UX taraması 2026-10-08,
// 600 satır sınırı). Üstte hücre özeti (`dl`); M17: iç aktörde altında "Sürümler (N)" listesi
// (`GET /kpi/values/:id/versions`, en yeni önce, ilk 5 + "Tümünü göster") — özetteki sürüm/kim/
// tarih satırları listeyle tekrar etmesin diye düşer. Portalda liste YOK (iç tarihçe müşteriye
// açılmaz); `noVersions` cümlesi yalnız portalda; iç not satırı yok (`portalHistoryRows`).
// Dalga 3: müşteri notu/açıklama yanıtı artık hücre DTO'sunda değil, not iş parçacığında — iç aktörde
// özetin altında thread (salt okunur; yanıt alanı Değer sekmesinde / not penceresinde) görünür.

import { useCallback, useEffect, useState } from "react";
import type { KpiResolvedCell, KpiValueVersion } from "@mahir/shared";
import { listKpiValueVersions } from "../../api/kpi";
import { VERSION_LIST_INITIAL, versionLines } from "../../lib/kpi-cell-history";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import { historyRows } from "../../lib/kpi-evidence-ui";
import { KPI_COPY } from "../../lib/kpi-ui";
import { portalHistoryRows } from "../../lib/portal-kpi-ui";
import type { NoteCellRef } from "../../api/kpi-notes";
import { KpiCellNotes } from "./KpiCellNotes";

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; versions: KpiValueVersion[] };

export function KpiCellHistory(p: {
  cell: KpiResolvedCell | undefined;
  indicator: { unit: string | null; valueType?: string | null | undefined };
  currentUserId: string | null;
  isClient: boolean;
  /** Dalga 3 — iç aktör: hücrenin not iş parçacığı (salt okunur) */
  notes?: { setId: string; cell: NoteCellRef; onChanged: () => void } | undefined;
  onClose: () => void;
}): JSX.Element {
  const H = KPI_EVIDENCE_COPY.history;
  const raw = historyRows(p.cell, p.currentUserId);
  const rows = p.isClient
    ? portalHistoryRows(raw, { note: H.note, clarificationNote: H.clarificationNote })
    : raw.filter((r) => r.label !== H.lastUpdate && r.label !== H.by && r.label !== H.version);
  const valueId = p.cell?.valueId;
  const showList = !p.isClient && !!valueId;
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async (id: string, alive: () => boolean): Promise<void> => {
    setState({ kind: "loading" });
    try {
      const versions = await listKpiValueVersions(id);
      if (alive()) setState({ kind: "ready", versions });
    } catch {
      if (alive()) setState({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    if (!showList || !valueId) {
      setState({ kind: "idle" });
      return;
    }
    let alive = true;
    setShowAll(false);
    void load(valueId, () => alive);
    return () => {
      alive = false;
    };
  }, [showList, valueId, load]);

  return (
    <div data-testid="kpi-cell-history">
      {rows.length === 0 && !showList ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {H.empty}
        </div>
      ) : rows.length > 0 ? (
        <dl
          className="grid text-xs"
          style={{ gridTemplateColumns: "110px 1fr", rowGap: 4, columnGap: 8, margin: 0 }}
        >
          {rows.map((r) => (
            <div key={r.label} style={{ display: "contents" }}>
              <dt style={{ color: "var(--color-text-3)" }}>{r.label}</dt>
              <dd style={{ margin: 0, color: "var(--color-text)" }}>{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {showList ? (
        <VersionList
          state={state}
          showAll={showAll}
          onShowAll={() => setShowAll(true)}
          onRetry={() => {
            if (valueId) void load(valueId, () => true);
          }}
          unit={p.indicator.unit}
          percent={p.indicator.valueType === "percent"}
          currentUserId={p.currentUserId}
        />
      ) : null}
      {p.isClient ? (
        <div className="text-xs mt-2" style={{ color: "var(--color-text-3)" }}>
          {H.noVersions}
        </div>
      ) : null}
      {!p.isClient && p.notes ? (
        <div className="mt-3">
          <KpiCellNotes
            audience="internal"
            setId={p.notes.setId}
            cell={p.notes.cell}
            composer={false}
            onChanged={p.notes.onChanged}
          />
        </div>
      ) : null}
      <div
        className="flex justify-end mt-3"
        style={{
          position: "sticky",
          bottom: 0,
          background: "var(--color-surface)",
          paddingTop: 6,
        }}
      >
        <button
          type="button"
          onClick={p.onClose}
          className="px-3 py-1.5 rounded-lg text-sm"
          style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
        >
          {KPI_EVIDENCE_COPY.reported.close}
        </button>
      </div>
    </div>
  );
}

function VersionList(p: {
  state: LoadState;
  showAll: boolean;
  onShowAll: () => void;
  onRetry: () => void;
  unit: string | null;
  percent: boolean;
  currentUserId: string | null;
}): JSX.Element {
  const C = KPI_COPY.cellHistory;
  const muted = { color: "var(--color-text-3)" };
  const versions = p.state.kind === "ready" ? p.state.versions : [];
  const title = p.state.kind === "ready" ? `${C.title} (${versions.length.toString()})` : C.title;
  const lines = versionLines(versions, {
    unit: p.unit,
    percent: p.percent,
    currentUserId: p.currentUserId,
  });
  const visible = p.showAll ? lines : lines.slice(0, VERSION_LIST_INITIAL);
  const hidden = lines.length - visible.length;
  return (
    <div className="mt-3" data-testid="kpi-cell-versions">
      <div className="text-xs font-semibold mb-1" style={{ color: "var(--color-text)" }}>
        {title}
      </div>
      {p.state.kind === "loading" || p.state.kind === "idle" ? (
        <div className="text-xs" role="status" style={muted}>
          {C.loading}
        </div>
      ) : p.state.kind === "error" ? (
        <div className="text-xs flex items-center gap-2" role="alert">
          <span style={{ color: "var(--color-error)" }}>{C.error}</span>
          <button
            type="button"
            onClick={p.onRetry}
            className="underline"
            style={{ color: "var(--color-primary)" }}
          >
            {C.retry}
          </button>
        </div>
      ) : lines.length === 0 ? (
        <div className="text-xs" style={muted}>
          {C.empty}
        </div>
      ) : (
        <>
          <ol
            aria-label={C.title}
            className="text-xs"
            style={{ listStyle: "none", margin: 0, padding: 0 }}
          >
            {visible.map((l) => (
              <li
                key={l.id}
                title={l.title}
                style={{ padding: "4px 0", borderTop: "1px solid var(--color-border)" }}
              >
                <div style={{ color: "var(--color-text)", fontWeight: 600 }}>{l.head}</div>
                <div style={muted}>
                  {l.meta}
                  {l.change ? (
                    <span style={{ color: "var(--color-text-2)" }}> · {l.change}</span>
                  ) : null}
                </div>
                {l.event ? (
                  <div
                    style={{ color: "var(--color-text-2)" }}
                    data-testid="kpi-version-unit-event"
                  >
                    {l.event}
                  </div>
                ) : null}
                {l.reason ? <div style={{ color: "var(--color-text-2)" }}>{l.reason}</div> : null}
              </li>
            ))}
          </ol>
          {hidden > 0 ? (
            <div className="text-xs mt-1" style={muted}>
              {C.more(hidden)} ·{" "}
              <button
                type="button"
                onClick={p.onShowAll}
                className="underline"
                style={{ color: "var(--color-primary)" }}
              >
                {C.showAll}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
